import { GridColumn, GridRow } from 'app/layouts/grid';
import { Button } from 'app/shared/button';
import { DetailItem } from 'app/shared/info-section/item';
import { MultiSelect } from 'app/shared/select';
import { TreeContext } from 'contexts/automation';
import { useAutomationStepService } from 'hooks/automation/steps';
import React, { useContext, useEffect, useState } from 'react';
import styles from './config.module.css';
import { SimpleInput } from 'app/shared/input';
import { toast } from 'react-toastify';

const VALUE_TYPES = [
  { label: 'Text', value: 'string' },
  { label: 'Number', value: 'number' },
  { label: 'Boolean', value: 'boolean' }
];

const RULES = {
  string: [
    { label: 'Equals', value: 'equal' },
    { label: 'Not Equal', value: 'not_equal' },
    { label: 'Contains', value: 'contains' },
    { label: 'Does Not Contain', value: 'not_contain' }
  ],
  number: [
    { label: 'Equals', value: 'equal' },
    { label: 'Not Equal', value: 'not_equal' },
    { label: 'Greater Than', value: 'greater_than' },
    { label: 'Less Than', value: 'less_than' },
    { label: 'Range', value: 'range' }
  ],
  boolean: [
    { label: 'Is True', value: 'is_true' },
    { label: 'Is False', value: 'is_false' }
  ]
};

const RangeInput = ({ default_value = '', onChange }) => {
  const [min, setMin] = useState(0);
  const [max, setMax] = useState(0);

  useEffect(() => {
    onChange(`${min}:${max}`);
  }, [min, max]);

  useEffect(() => {
    if (!default_value || min || max) return;
    const [default_min, default_max] = default_value.split(':');
    setMin(default_min);
    setMax(default_max);
  }, [default_value, min, max]);

  return (
    <GridRow num_of_columns={2}>
      <GridColumn>
        <DetailItem title="Min">
          <SimpleInput type="number" value={min} onInput={(val) => setMin(val)} />
        </DetailItem>
      </GridColumn>
      <GridColumn>
        <DetailItem title="Max">
          <SimpleInput type="number" value={max} onInput={(val) => setMax(val)} />
        </DetailItem>
      </GridColumn>
    </GridRow>
  );
};

export const AutomationBranchConfig = () => {
  const { step_modal, toggleStepModal } = useContext(TreeContext);

  const { createStep, updateStep } = useAutomationStepService();

  const [property, setProperty] = useState('');
  const [selected_rule, setSelectedRule] = useState({});
  const [selected_value_type, setSelectedValueType] = useState(VALUE_TYPES[0]);
  const [option_value, setOptionValue] = useState('');

  const [creating, setCreating] = useState(false);

  //
  const is_edit_mode = step_modal.mode === 'edit';

  useEffect(() => {
    if (!is_edit_mode) return;
    const { metadata } = step_modal.data;
    const { value_type, rule, option_value: opt_value } = metadata;
    setSelectedValueType(VALUE_TYPES.find((type) => type.value === value_type) || VALUE_TYPES[0]);
    setSelectedRule(RULES[value_type].find((rul) => rul.value === rule) || RULES[value_type][0]);
    setOptionValue(opt_value);
  }, [is_edit_mode, step_modal.data]);

  const handleValueTypeChange = (value) => {
    setSelectedValueType(value);
    setSelectedRule(RULES[value.value][0]);
    setOptionValue('');
  };

  const handleRuleChange = (rule) => {
    setSelectedRule(rule);
    setOptionValue('');
  };

  const submit = async () => {
    const data = {
      children: [],
      parents: step_modal.parents,
      source: step_modal.source,
      template_id: step_modal.template_id,
      type: 'branch',
      branch_value: `${selected_value_type.value}:${selected_rule.value}:${option_value}`,
      metadata: {
        type: 'branch',
        value_type: selected_value_type.value,
        rule: selected_rule.value,
        rule_label: selected_rule.label,
        option_value,
        property
      }
    };

    try {
      const step_id = is_edit_mode ? step_modal.data.id : null;
      setCreating(() => true);
      const result = step_id ? await updateStep(step_id, data) : await createStep(data);
      if (result) {
        setSelectedRule({});
        setSelectedValueType(VALUE_TYPES[0]);
        setOptionValue('');
        toggleStepModal({ show: false });
      }
    } catch (error) {
      // Handle error if needed
      toast.error('An error occurred while saving the branch step.');
    } finally {
      setCreating(() => false);
    }
  };

  return (
    <div className={styles.formWrapper}>
      <GridRow num_of_columns={1}>
        <GridColumn>
          <DetailItem title="Property">
            <SimpleInput value={property} onInput={setProperty} />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={1}>
        <GridColumn>
          <DetailItem title="Value Type">
            <MultiSelect
              options={VALUE_TYPES}
              onChange={handleValueTypeChange}
              value={selected_value_type}
            />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={1}>
        <GridColumn>
          <DetailItem title="Rule">
            <MultiSelect
              options={RULES[selected_value_type.value]}
              onChange={handleRuleChange}
              value={selected_rule}
            />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={1}>
        <GridColumn>
          {selected_rule.value !== 'range' ? (
            <DetailItem title="Option Values">
              <SimpleInput
                disabled={selected_value_type.value === 'boolean'}
                type={selected_value_type.value === 'number' ? 'number' : 'text'}
                value={option_value}
                onInput={setOptionValue}
              />
            </DetailItem>
          ) : (
            <RangeInput default_value={option_value} onChange={setOptionValue} />
          )}
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={3}>
        <GridColumn />
        <GridColumn />
        <GridColumn>
          <Button text="Save" disabled={creating} onClick={submit} />
        </GridColumn>
      </GridRow>
    </div>
  );
};
